// src/shared/components/Input.tsx
import React, { forwardRef, useId } from 'react';
import { motion } from 'framer-motion';
import { persianToEnglishNumbers } from '@core/utils/numberUtils';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  suffix?: React.ReactNode;
  prefix?: React.ReactNode;
  action?: React.ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      fullWidth = false,
      leftIcon,
      rightIcon,
      suffix,
      prefix,
      action,
      className = '',
      onChange,
      type: typeProp = 'text',
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = props.id ?? `input-${generatedId.replace(/:/g, '')}`;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;
    const describedBy = [props['aria-describedby'], error ? errorId : helperText ? helperId : null].filter(Boolean).join(' ') || undefined;
    const isNumeric = typeProp === 'number';
    const type = isNumeric ? 'text' : typeProp;
    const inputMode = isNumeric ? ('decimal' as const) : (props as any).inputMode;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Convert Persian/Arabic digits to English on all inputs (project-wide)
      const raw = e.target.value;
      const converted = persianToEnglishNumbers(raw);
      if (converted !== raw) {
        e.target.value = converted;
      }
      onChange?.(e);
    };
    const hasLeftContent = leftIcon || prefix;
    const hasRightContent = rightIcon || suffix || action;
    
    return (
      <div className={`min-w-0 ${fullWidth ? 'w-full' : ''}`}>
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-1">
            {label}
            {props.required && <span className="text-red-500 mr-1">*</span>}
          </label>
        )}
        {suffix ? (
          <div className={`flex rounded-lg border overflow-hidden ${error ? 'border-red-500' : 'border-gray-300'}`}>
            <div className="flex-[0_0_33.333%] flex items-stretch min-w-0 pointer-events-none [&>*]:pointer-events-auto [&>*]:w-full [&>*]:min-w-0 [&>*]:min-h-0 [&>*]:self-stretch">
              {suffix}
            </div>
            <input
              ref={ref}
              type={type}
              inputMode={inputMode}
              onChange={handleChange}
              className={`
                flex-1 min-w-0 border-0 rounded-none py-2 min-h-11
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset
                transition-all duration-200
                ${hasLeftContent ? 'pr-10' : 'px-4'}
                ${hasRightContent ? 'pl-10' : (hasLeftContent ? '' : 'px-4')}
                ${props.disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
                ${className}
              `}
              {...props}
              id={inputId}
              aria-invalid={error ? true : props['aria-invalid']}
              aria-describedby={describedBy}
            />
          </div>
        ) : (
          <div className="relative">
            {leftIcon && !prefix && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none z-10">
                {leftIcon}
              </div>
            )}
            {prefix && (
              <div className="absolute right-0 top-0 bottom-0 flex items-center pr-1 z-10">
                {prefix}
              </div>
            )}
            <input
              ref={ref}
              type={type}
              inputMode={inputMode}
              onChange={handleChange}
              className={`
                w-full min-w-0 py-2 min-h-11 border rounded-lg
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                transition-all duration-200
                ${error ? 'border-red-500' : 'border-gray-300'}
                ${hasLeftContent ? 'pr-10' : 'px-4'}
                ${hasRightContent ? 'pl-10' : (hasLeftContent ? '' : 'px-4')}
                ${props.disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
                ${className}
              `}
              {...props}
              id={inputId}
              aria-invalid={error ? true : props['aria-invalid']}
              aria-describedby={describedBy}
            />
            {action && (
              <div className="absolute left-1.5 inset-y-0 flex items-center z-10">
                {action}
              </div>
            )}
            {rightIcon && !suffix && !action && (
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none z-10">
                {rightIcon}
              </div>
            )}
          </div>
        )}
        {error && (
          <motion.p
            id={errorId}
            role="alert"
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-1 text-sm text-red-600"
          >
            {error}
          </motion.p>
        )}
        {helperText && !error && (
          <p id={helperId} className="mt-1 text-sm text-gray-500">{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
